import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Total unread count across all threads (same as totalCount for UI "Messages" header)
  totalUnreadCount: 0,
  totalCount: 0,
  // Count per thread: { threadId: count }
  threadCounts: {},
  // Last message timestamp per thread for sorting
  lastMessageTimestamps: {},
  // Separate counts for different message types / roleBased from API
  accountExecutiveCount: 0,
  superAdminCount: 0,
  inboxCount: 0,
  companyAdminCount: 0,
  workerCount: 0,
};

const messageCountSlice = createSlice({
  name: 'messageCount',
  initialState,
  reducers: {
    // Initialize thread count (when thread is created or loaded)
    initializeThreadCount: (state, action) => {
      const { threadId, initialCount = 0 } = action.payload;
      
      state.threadCounts[threadId] = initialCount;
      state.lastMessageTimestamps[threadId] = new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
      state.totalCount = state.totalUnreadCount;
    },

    // Increment count for a specific thread (when receiving a message)
    incrementThreadCount: (state, action) => {
      const { threadId, timestamp } = action.payload;
      
      if (!state.threadCounts[threadId]) {
        state.threadCounts[threadId] = 0;
      }
      
      state.threadCounts[threadId] += 1;
      state.lastMessageTimestamps[threadId] = timestamp || new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
      state.totalCount = state.totalUnreadCount;
    },

    // Decrement count for a specific thread (when sending a message)
    decrementThreadCount: (state, action) => {
      const { threadId, timestamp } = action.payload;
      
      if (state.threadCounts[threadId] && state.threadCounts[threadId] > 0) {
        state.threadCounts[threadId] -= 1;
      }
      
      state.lastMessageTimestamps[threadId] = timestamp || new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
      state.totalCount = state.totalUnreadCount;
    },

    // Clear count for a specific thread (when thread is opened/read)
    clearThreadCount: (state, action) => {
      const { threadId } = action.payload;
      
      if (state.threadCounts[threadId]) {
        state.threadCounts[threadId] = 0;
      }
      
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
    },

    // Clear all counts (when user logs out)
    clearAllCounts: (state) => {
      state.totalUnreadCount = 0;
      state.totalCount = 0;
      state.threadCounts = {};
      state.lastMessageTimestamps = {};
    },

    // Set count for a specific thread (when loading from server)
    setThreadCount: (state, action) => {
      const { threadId, count, timestamp } = action.payload;
      
      state.threadCounts[threadId] = count || 0;
      state.lastMessageTimestamps[threadId] = timestamp || new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
      state.totalCount = state.totalUnreadCount;
    },

    // Update multiple thread counts (when loading threads from server)
    updateMultipleThreadCounts: (state, action) => {
      const { threadCounts } = action.payload;
      
      state.threadCounts = {};
      state.lastMessageTimestamps = {};
      Object.entries(threadCounts).forEach(([threadId, data]) => {
        state.threadCounts[threadId] = data.count || 0;
        state.lastMessageTimestamps[threadId] = data.timestamp || new Date().toISOString();
      });
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
      state.totalCount = state.totalUnreadCount;
    },

    // Get count for a specific thread
    getThreadCount: (state, action) => {
      const { threadId } = action.payload;
      return state.threadCounts[threadId] || 0;
    },

    // Set count for specific message types
    setAccountExecutiveCount: (state, action) => {
      state.accountExecutiveCount = action.payload;
    },

    setSuperAdminCount: (state, action) => {
      state.superAdminCount = action.payload;
    },

    setInboxCount: (state, action) => {
      state.inboxCount = action.payload;
    },

    // Update counts based on message type
    updateMessageTypeCount: (state, action) => {
      const { messageType, count } = action.payload;
      
      switch (messageType) {
        case 'account-executive':
        case 'account_executive':
          state.accountExecutiveCount = count;
          break;
        case 'super-admin':
        case 'super_admin':
          state.superAdminCount = count;
          break;
        case 'inbox':
        case 'company_admin':
          state.inboxCount = count;
          state.companyAdminCount = count;
          break;
        case 'worker':
          state.workerCount = count;
          break;
        default:
          break;
      }
    },

    // Set full unread state from API (GET /api/messages/unread-counts/detailed)
    setUnreadCounts: (state, action) => {
      const { total, threads, roleBased } = action.payload ?? {};
      if (typeof total === 'number') {
        state.totalUnreadCount = total;
        state.totalCount = total;
      }
      if (threads) {
        if (Array.isArray(threads)) {
          threads.forEach((item) => {
            const id = String(item.threadId ?? item.id);
            state.threadCounts[id] = item.unreadCount ?? item.count ?? 0;
          });
        } else if (typeof threads === 'object') {
          Object.entries(threads).forEach(([id, count]) => {
            state.threadCounts[String(id)] = count ?? 0;
          });
        }
        state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, c) => sum + c, 0);
        state.totalCount = state.totalUnreadCount;
      }
      if (roleBased && typeof roleBased === 'object') {
        if (typeof roleBased.account_executive === 'number') state.accountExecutiveCount = roleBased.account_executive;
        if (typeof roleBased.company_admin === 'number') {
          state.inboxCount = roleBased.company_admin;
          state.companyAdminCount = roleBased.company_admin;
        }
        if (typeof roleBased.worker === 'number') state.workerCount = roleBased.worker;
        if (typeof roleBased.super_admin === 'number') state.superAdminCount = roleBased.super_admin;
      }
    },

    clearThreadUnreadCount: (state, action) => {
      const { threadId } = action.payload ?? {};
      if (threadId) {
        state.threadCounts[String(threadId)] = 0;
        state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, c) => sum + c, 0);
        state.totalCount = state.totalUnreadCount;
      }
    },

    updateCount: (state, action) => {
      const { type, threadId, timestamp } = action.payload ?? {};
      if (type === 'increment' && threadId) {
        state.threadCounts[String(threadId)] = (state.threadCounts[String(threadId)] ?? 0) + 1;
        state.lastMessageTimestamps[String(threadId)] = timestamp || new Date().toISOString();
        state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, c) => sum + c, 0);
        state.totalCount = state.totalUnreadCount;
      }
    },
  },
});

export const {
  initializeThreadCount,
  incrementThreadCount,
  decrementThreadCount,
  clearThreadCount,
  clearAllCounts,
  setThreadCount,
  setUnreadCounts,
  clearThreadUnreadCount,
  updateCount,
  updateMultipleThreadCounts,
  getThreadCount,
  setAccountExecutiveCount,
  setSuperAdminCount,
  setInboxCount,
  updateMessageTypeCount,
} = messageCountSlice.actions;

export default messageCountSlice.reducer;
